/**
 * CurrencyValue — renders a formatted monetary value with an inline
 * currency toggle pill. Tap the pill to cycle through currencies.
 *
 * CurrencyToggleBar — header toggle with dropdown for 18 Pan-African currencies.
 */
import { useState, useRef, useEffect } from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowRightLeft, ChevronDown, Check } from "lucide-react";

export function CurrencyValue({
    amount,
    baseCurrency = "KES",
    className = "",
    showToggle = true,
    size = "sm",
    testId,
}) {
    const { formatMoney, cycleCurrency, currency } = useCurrency();
    const formatted = formatMoney(amount, baseCurrency);

    const pillSize = size === "md"
        ? "h-5 px-1.5 text-[9px] gap-0.5"
        : "h-4 px-1 text-[8px] gap-px";
    const iconSize = size === "md" ? "w-2.5 h-2.5" : "w-2 h-2";

    return (
        <span className={`inline-flex items-center gap-1 ${className}`} data-testid={testId}>
            <span className="proportional-nums">{formatted}</span>
            {showToggle && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); cycleCurrency(); }}
                    className={`inline-flex items-center ${pillSize} rounded-full border border-border/60 bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0`}
                    title={`Switch currency (${currency})`}
                    aria-label={`Switch currency from ${currency}`}
                    data-testid={testId ? `${testId}-toggle` : "currency-toggle"}
                >
                    <ArrowRightLeft className={iconSize} strokeWidth={2} />
                    <span className="font-bold leading-none">{currency}</span>
                </button>
            )}
        </span>
    );
}

/**
 * CurrencyToggleBar — standalone toggle bar for headers.
 * Shows quick-access buttons (KES/USD/EUR) + a dropdown for all 18 currencies.
 */
export function CurrencyToggleBar({ className = "" }) {
    const { currency, setCurrency, SUPPORTED, CURRENCY_META } = useCurrency();
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Quick-access currencies shown as buttons
    const QUICK = ["KES", "USD", "EUR"];

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Group currencies for the dropdown
    const groups = {};
    for (const code of SUPPORTED) {
        const meta = CURRENCY_META?.[code] || {};
        const group = meta.group || "Other";
        if (!groups[group]) groups[group] = [];
        groups[group].push({ code, ...meta });
    }

    return (
        <div
            ref={dropdownRef}
            className={`relative inline-flex items-center rounded-full border border-border/60 overflow-visible ${className}`}
            data-testid="currency-toggle-bar"
        >
            {/* Quick-access buttons */}
            {QUICK.map((c) => (
                <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    className={`px-2.5 py-1 text-[10px] font-bold tracking-wide transition-colors ${
                        currency === c
                            ? "bg-primary text-primary-foreground"
                            : "bg-secondary/40 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`}
                    data-testid={`currency-btn-${c}`}
                    aria-pressed={currency === c}
                >
                    {c}
                </button>
            ))}

            {/* Dropdown trigger for all currencies */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`px-2 py-1 text-[10px] font-bold tracking-wide transition-colors flex items-center gap-0.5 ${
                    !QUICK.includes(currency)
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary/40 text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
                data-testid="currency-dropdown-trigger"
                aria-expanded={open}
            >
                {!QUICK.includes(currency) ? currency : ""}
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown panel — solid card fill (Sept 2026 fix: previous inline
                `var(--card, ...)` was invalid CSS since --card is stored as
                bare HSL components, not a usable colour on its own, so the
                panel had no real background and page text bled through on
                scroll). Uses the same solid `bg-card` fill as the Cookie
                preferences panel. */}
            {open && (
                <div
                    className="absolute top-full right-0 mt-1 z-[60] min-w-[240px] max-h-[360px] overflow-y-auto rounded-lg border border-border bg-card shadow-xl"
                    data-testid="currency-dropdown-panel"
                >
                    {Object.entries(groups).map(([groupName, items]) => (
                        <div key={groupName}>
                            <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-muted-foreground font-bold sticky top-0 bg-card">
                                {groupName}
                            </div>
                            {items.map((item) => (
                                <button
                                    key={item.code}
                                    type="button"
                                    onClick={() => { setCurrency(item.code); setOpen(false); }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                                        currency === item.code
                                            ? "bg-primary/10 text-primary"
                                            : "hover:bg-secondary/60 text-foreground"
                                    }`}
                                    data-testid={`currency-option-${item.code}`}
                                >
                                    <span className="text-sm shrink-0">{item.flag || ""}</span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-xs font-bold font-mono">{item.code}</span>
                                            <span className="text-[10px] text-muted-foreground truncate">{item.name}</span>
                                        </div>
                                    </div>
                                    {currency === item.code && (
                                        <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
